'use client';

import Script from 'next/script';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import './seo-styles.css';

export function SeoView({
  content,
  script,
  className,
  showAll,
  doNotUseHeading,
}: {
  content: string;
  script?: string;
  className?: string;
  showAll?: boolean;
  doNotUseHeading?: boolean;
}) {
  const ref = useRef<null | HTMLDivElement>(null);
  const [showMore, setShowMore] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const { sanitizedHtml, heading } = useMemo(() => {
    const sanitized = content?.replace(/style="[^"]*"/gi, '') || '';
    if (!sanitized) {
      return { sanitizedHtml: '', heading: '' };
    }

    const match = sanitized.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
    if (!match) {
      return { sanitizedHtml: sanitized, heading: '' };
    }

    if (doNotUseHeading) {
      const withoutH1 = sanitized.replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, '');
      return { sanitizedHtml: withoutH1, heading: '' };
    }

    const headingInnerHtml = match[1] ?? '';
    const headingText = headingInnerHtml.replace(/<[^>]*>/g, '').trim();
    const withoutHeading = sanitized.replace(match[0], '');

    return { sanitizedHtml: withoutHeading, heading: headingText };
  }, [content]);

  useLayoutEffect(() => {
    if (showAll) {
      setShowMore(true);
    }
    setTimeout(() => {
      if ((ref?.current?.clientHeight || 0) < (ref?.current?.scrollHeight || 0) && !showAll) {
        setShowLink(true);
      }
    }, 500);
  }, [ref]);

  const onClickMore = () => {
    setShowMore((prev) => {
      if (prev) {
        window?.scrollTo({
          behavior: 'smooth',
          top: (ref?.current?.offsetTop || 0) - 200,
        });
      }
      return !prev;
    });
  };

  return (
    <section className="mb-12">
      {script && script !== '' && (
        <Script
          id="json-ld-seo"
          type="application/ld+json"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: script,
          }}
        />
      )}
      {(heading || sanitizedHtml !== '') && (
        <>
          {heading && (
            <h1
              className={`seo-content mt-6 px-4 md:px-0 text-[.875rem] ${className ?? ''} sm:text-[1.125rem] md:text-[1.5rem] lg:text-[2rem] text-[#212121] font-semibold font-fraunces`}
            >
              {heading}
            </h1>
          )}
          {sanitizedHtml !== '' && (
            <div
              ref={ref}
              className={`seo-content ${heading ? 'mt-0' : 'mt-6'} px-4 md:px-0 ${className ?? ''} ${showMore ? '' : 'seo-content-container'}`}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          )}
          {sanitizedHtml !== '' && showLink && (
            <div className={`seo-content-more px-4 md:px-0 ${className ?? ''}`}>
              <span className={`underline text-hzc_purple_500`} onClick={onClickMore}>
                {showMore ? (
                  <>
                    <i className="sm_arrow sm_up"></i>daha az göster
                  </>
                ) : (
                  <>
                    <i className="sm_arrow sm_right"></i>devamını oku
                  </>
                )}
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
